// KaziPeople Sign — the signature pad. A full-width canvas the signer draws on
// with finger, stylus or mouse (pointer events cover all three). Exports a
// transparent PNG. Deliberately generous: signing in a tiny box is the #1
// mobile e-sign complaint, so the pad takes the full modal width.
import React, { useEffect, useRef, useState } from 'react';

export interface SignaturePadHandle {
  isEmpty: () => boolean;
  toPNG: () => string | null; // base64 (no data: prefix), null if empty
  clear: () => void;
}

export const SignaturePad: React.FC<{
  height?: number;
  onChange?: (empty: boolean) => void;
  padRef: React.MutableRefObject<SignaturePadHandle | null>;
}> = ({ height = 180, onChange, padRef }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const [, force] = useState(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Match the canvas buffer to its CSS size × devicePixelRatio for crisp ink.
    const scale = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(scale, scale);
      ctx.lineWidth = 2.4;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#1a1a1a';
    }
  }, [height]);

  const pos = (e: React.PointerEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e: React.PointerEvent) => {
    e.preventDefault();
    canvasRef.current?.setPointerCapture(e.pointerId);
    drawing.current = true;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e: React.PointerEvent) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    if (!hasInk.current) {
      hasInk.current = true;
      onChange?.(false);
      force((n) => n + 1);
    }
  };
  const end = () => {
    drawing.current = false;
  };

  padRef.current = {
    isEmpty: () => !hasInk.current,
    toPNG: () => {
      if (!hasInk.current || !canvasRef.current) return null;
      const dataUrl = canvasRef.current.toDataURL('image/png');
      return dataUrl.split(',')[1] ?? null;
    },
    clear: () => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      hasInk.current = false;
      onChange?.(true);
      force((n) => n + 1);
    },
  };

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height, touchAction: 'none' }}
        className="rounded-xl border-2 border-dashed border-border bg-white dark:bg-neutral-100"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        aria-label="Signature pad — draw your signature"
      />
      {!hasInk.current && (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
          Draw your signature here
        </span>
      )}
      <button
        type="button"
        onClick={() => padRef.current?.clear()}
        className="absolute right-2 top-2 rounded-md border border-border bg-white/80 px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground"
      >
        Clear
      </button>
    </div>
  );
};
